import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import areasMapper from '../../../Utils/AreasMapper.js'
import countryMapper from '../../../Utils/CountriesMapper.js'
import levelsMapper from '../../../Utils/LevelsMapper.js'
import subareasMapper from '../../../Utils/SubareasMapper.js'
import jobMatch from '../../../API/JobMatch/jobMatch'

import { useIntl } from 'react-intl'
import List from '../../../API/Lists/Lists.js'

import { translate as translateAreas } from '../../../i18n/Library/areasCorrelation.js'
import { translate as translatePosition } from '../../../i18n/Library/hierarchicalLevel'
import { SalarySelectorModal } from './SalarySelectorModal'

const loggedEmail = localStorage.getItem('email')
const isMayekawa = !!loggedEmail?.includes('@mayekawa')

export function FormJobMatch({ handleChange, user, isOpen, onClose, byPosition }) {
	const getLanguage = useIntl()
	const language = getLanguage.locale === 'PT-BR' ? 'pt' : 'es'
	const companyName = JSON.parse(localStorage.getItem('user'))?.name

	const [countries, setCountries] = useState([])
	const [areas, setAreas] = useState([])
	const [levels, setLevels] = useState([])
	const [selectedSalary, setSelectedSalary] = useState(null)
	const [adjustedSalaryOptions, setAdjustedSalaryOptions] = useState([])
	const [factors, setFactors] = useState([])
	const [marketSalaries, setMarketSalaries] = useState([])
	const [isLoading, setIsLoading] = useState(false)

	const formattedUserByColaborator = {
		position: user?.jobname,
		country: user?.country?.id,
		area: user?.area?.id,
		level: user?.level?.id,
		salary: user?.monthly_salary,
		englishLevel: user?.english_level_id?.id,
		formationId: user?.qualification_id,
		experience: user?.seniority_id,
		mydna_email_position: user?.mydna_email_position,
	}

	const formattedUserByPosition = {
		position: user?.job_name,
		country: user?.country,
		area: user?.area_id,
		level: user?.level_id,
		salary: user?.salary,
		englishLevel: user?.english_level,
		formationId: user?.qualification,
		experience: user?.seniority_level,
		mydna_email_position: user?.mydna_email_position,
		byPosition: true,
	}

	const formattedUser = byPosition ? formattedUserByPosition : formattedUserByColaborator

	const initializeData = useCallback(async () => {
		const [resArea, resCountry, resLevel, resFactors] = await Promise.all([
			List.areas(),
			List.countries(),
			List.levels(),
			jobMatch.getFactors(companyName),
		])

		setCountries(resCountry.map(countryMapper.toDomain))
		setAreas(resArea.map(areasMapper.toDomain))
		setLevels(resLevel.map(levelsMapper.toDomain))
		setFactors(resFactors)
	}, [])

	const getMarketSalarios = useCallback(async () => {
		setIsLoading(true)
		const res = await jobMatch.getMarketSalaries(formattedUser.country, formattedUser.level, formattedUser.area, companyName)

		setMarketSalaries(res.length > 0 ? res : [])
		setAdjustedSalaryOptions([])

		if (!factors || !res.length) return

		const size = factors?.company_info?.company_size_id

		const getFactor = (arr, level, sizeKey) => {
			if (!Array.isArray(arr)) return 1

			if (!level) return arr.find(f => f.level === 2 && (!sizeKey || f.size === sizeKey))?.factor || 1

			const match = arr.find(f => f.level === level && (!sizeKey || f.size === sizeKey))
			if (match) return match.factor

			const middle = arr.find(f => f.level === 2 && (!sizeKey || f.size === sizeKey))
			return middle?.factor || 1
		}

		const seniorityFactor = getFactor(factors.seniority_factors, formattedUser.experience, size)
		const qualificationFactor = getFactor(factors.qualification_factors, formattedUser.formationId)
		const languageFactor = getFactor(factors.language_factors, formattedUser.englishLevel)

		const adjusted = Array.isArray(res)
			? res.map(item => {
					const base = Number(item.salary)
					const adjustedValue = base * seniorityFactor * qualificationFactor * languageFactor
					return {
						id: item.id,
						name: item.position,
						fixedSalary: adjustedValue,
						variableSalary: item.variable_salary,
					}
			  })
			: []

		setAdjustedSalaryOptions(adjusted)
		setIsLoading(false)
	}, [user])

	useEffect(() => {
		initializeData()
		getMarketSalarios()
	}, [initializeData, getMarketSalarios])

	const handleSelect = option => {
		setSelectedSalary(option)
		handleSendJobMatch(option)
	}

	const translatedAreas = [...areas]
		.sort((a, b) => a.value.localeCompare(b.value))
		.map(item => ({
			...item,
			value: translateAreas[language][item?.value],
		}))

	const translatedLevels = [...levels]
		.sort((a, b) => a.value.localeCompare(b.value))
		.map(item => ({
			...item,
			value: translatePosition[language][item?.value],
		}))

	const handleSendJobMatch = async option => {
		try {
			setIsLoading(true)

			const data = {
				job_id: option?.id,
				salary_market: Math.floor(Number(option?.fixedSalary)),
				mydna_email_position: formattedUser?.mydna_email_position,
				company_name: companyName,
			}
			await jobMatch.putJobMatch(data)

			onClose()
			handleChange()
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<>
			{!isMayekawa && (
				<SalarySelectorModal
					isOpen={isOpen}
					onClose={onClose}
					onSelect={handleSelect}
					options={adjustedSalaryOptions}
					selected={selectedSalary?.id}
					user={formattedUser}
					areaList={translatedAreas}
					levelList={translatedLevels}
					isLoading={isLoading}
					position={true}
				/>
			)}
		</>
	)
}
